import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { createErrorControlMap, ErrorControl, DxoFormStep } from '@dxo/ng-swift-ui';
import { Constants } from '../../billing-page/billing-page.types';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { MemberClient, MemberDetailResponse, GroupClient } from '@benefitsui/app/api/benefits-clients';
import { EmployeeData } from '../employee-page.types';
import { CurrencyPipe } from '@angular/common';
import { DropdownOption } from '@benefitsui/app/lib/common.types';
import { currencyStringCreate } from '@benefitsui/app/shared/utilities/currency-utilities';
import { StateUtilities } from '@benefitsui/app/shared/utilities/state-utilities';
@Component({
  selector: 'app-add-employee',
  templateUrl: './add.component.html',
  styleUrls: ['./add.component.scss'],
  standalone: false
})
export class AddEmployeeComponent implements OnInit {
  memberClient = inject(MemberClient);
  groupClient = inject(GroupClient);
  // SSN from query params
  ssnValue: string = '';
  memberId: string = '';
  originalEmployeeData: EmployeeData | undefined;
  // Modal state
  showCancelModal = false;
  // Tooltip state
  showTooltip = false;

  // Form stepper properties
  useGroups = false;
  currentStepIndex = 0;
  steps: DxoFormStep[] = [
    { id: 'personal', title: 'Personal information', valid: false, unfilledCount: 9, disabled: false },
    { id: 'employment', title: 'Employment details', valid: false, unfilledCount: 1, disabled: false },
    { id: 'coverage', title: 'Coverage elections', valid: false, unfilledCount: 0, disabled: true },
    { id: 'review', title: 'Review & submit', valid: false, unfilledCount: 0, disabled: true }
  ];

  // Form groups for each step
  personalInfoForm!: FormGroup;
  employmentForm!: FormGroup;
  contactForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const ssn = this.route.snapshot.queryParamMap.get('ssn');
    this.ssnValue = ssn || '';
    const memberId = this.route.snapshot.queryParamMap.get('memberId');
    this.memberId = memberId || '';
    if (this.memberId.length > 0) {
      // populate form if the person exists
      this.memberClient.demographics(this.memberId).subscribe((response) => {
        const employeeData = this.formatEmployee(response.data?.memberDetail);
        this.personalInfoForm.patchValue({
          firstName: employeeData.firstName,
          lastName: employeeData.lastName,
          middleName: employeeData.middleName,
          birthDate: employeeData.birthdate,
          hiredate: employeeData.hireDate,
          gender: employeeData.gender,
          address1: employeeData.address1,
          address2: employeeData.address2,
          city: employeeData.city,
          state: employeeData.state,
          zipCode: employeeData.zipCode,
          email: employeeData.email
        });
        this.originalEmployeeData = employeeData;
      });
    }

    this.initializeForms();
    this.setupFormValidation();

    this.loadBillDivisions();

    this.setupAnnualSalaryFormatter();
  }

  formatEmployee(input: MemberDetailResponse | undefined): EmployeeData {
    return {
      firstName: input?.firstName ?? Constants.StringEmpty,
      lastName: input?.lastName ?? Constants.StringEmpty,
      fullName: Constants.StringEmpty,
      middleName: input?.middleName ?? Constants.StringEmpty,
      birthdate: input?.dob ?? Constants.StringEmpty,
      gender: input?.gender ?? Constants.StringEmpty,
      status: input?.contractStatus ?? Constants.StringEmpty,
      occupation: input?.occupation ?? Constants.StringEmpty,
      salary: input?.salaries?.[0]?.annualSalary
        ? this.formatCurrency(input.salaries?.[0].annualSalary)
        : Constants.StringEmpty,
      hireDate: input?.hireDate ?? Constants.StringEmpty,
      smokerStatus: input?.smokerStatus ?? Constants.StringEmpty,
      billDivision: input?.billDivision ?? Constants.StringEmpty,
      billDivisionNumber: input?.jobSiteCode ?? Constants.StringEmpty,
      address1: input?.address1 ?? Constants.StringEmpty,
      address2: input?.address2 ?? Constants.StringEmpty,
      city: input?.city ?? Constants.StringEmpty,
      state: input?.state ?? Constants.StringEmpty,
      zipCode: input?.zipCode ?? Constants.StringEmpty,
      email: input?.email ?? Constants.StringEmpty,
      maskedssn: input?.maskedSsn ?? Constants.StringEmpty
    };
  }

  formatCurrency(amount: number | undefined): string {
    const currencyPipe = new CurrencyPipe('en-US');
    return currencyPipe.transform(amount) + Constants.StringEmpty;
  }

  // Custom Validators
  notSelectValidator = (control: AbstractControl): ValidationErrors | null => {
    if (control.value === 'Select' || control.value === '') {
      return { required: true };
    }
    return null;
  };

  birthDateValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = control.value;

    const dateFormatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (typeof value === 'string' && !dateFormatRegex.test(value)) {
      return { invalidFormat: true };
    }

    const birthDate = new Date(value);

    if (isNaN(birthDate.getTime())) {
      return { invalidFormat: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (birthDate >= today) {
      return { futureDate: true };
    }

    const ageInMilliseconds = today.getTime() - birthDate.getTime();
    const ageInYears = ageInMilliseconds / (1000 * 60 * 60 * 24 * 365.25);

    if (ageInYears < 10) {
      return { tooYoung: true };
    }

    return null;
  };

  initializeForms(): void {
    // Step 1: Personal Information - all fields (SSN removed as it's display-only)
    this.personalInfoForm = this.fb.group({
      birthDate: [Constants.StringEmpty, [Validators.required, this.birthDateValidator]],
      firstName: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      middleName: [
        Constants.StringEmpty,
        [Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      lastName: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      email: [Constants.StringEmpty, [Validators.email, Validators.minLength(6), Validators.maxLength(255)]],
      gender: [Constants.StringEmpty, [Validators.required]],
      smokerStatus: [Constants.StringEmpty],
      address1: [Constants.StringEmpty, [Validators.required]],
      address2: [Constants.StringEmpty],
      city: [Constants.StringEmpty, [Validators.required]],
      state: [Constants.StringEmpty, [Validators.required]],
      zipCode: [Constants.StringEmpty, [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]]
    });

    // Step 2: Employment Details
    this.employmentForm = this.fb.group({
      event: ['New hire', [Validators.required, this.notSelectValidator]],
      hireDate: [Constants.StringEmpty, [Validators.required, Validators.pattern(this.dateFormatRegex)]],
      employeeId: [Constants.StringEmpty, [Validators.maxLength(50), Validators.pattern(/^[a-zA-Z0-9]*$/)]],
      billDivision: [Constants.StringEmpty, [Validators.required, this.notSelectValidator]],
      annualSalary: [
        Constants.StringEmpty,
        [Validators.required, Validators.maxLength(15), Validators.pattern(/^[0-9$,.]+$/)]
      ],
      jobTitle: [Constants.StringEmpty, [Validators.maxLength(100)]]
    });

    // Step 3: Contact Information (reserved, maintain billDivision for compatibility/display helpers)
    this.contactForm = this.fb.group({
      billDivision: [Constants.StringEmpty]
    });

    // Keep the full form for compatibility
    this.addEmployeeForm = this.fb.group({
      firstName: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      middleName: [
        Constants.StringEmpty,
        [Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      lastName: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.NAME_MAX_LENGTH)]
      ],
      ssn: [
        Constants.StringEmpty,
        [Validators.required, Validators.pattern(this.ssnPatternRegex), Validators.maxLength(this.SSN_MAX_LENGTH)]
      ],
      birthDate: [Constants.StringEmpty, [Validators.required, Validators.pattern(this.dateFormatRegex)]],
      gender: [Constants.StringEmpty, [Validators.required]],
      hireDate: [Constants.StringEmpty, [Validators.required, Validators.pattern(this.dateFormatRegex)]],
      smokerStatus: [Constants.StringEmpty],
      occupation: [Constants.StringEmpty],
      annualSalary: [Constants.StringEmpty, [Validators.required]],
      billDivision: [Constants.StringEmpty, [Validators.required]]
    });

    this.formErrors = {
      ...this.getErrorMap(this.addEmployeeForm)
    };

    // Also create error controls for the individual forms
    const personalFormErrors = createErrorControlMap(this.personalInfoForm, {
      birthDate: {
        required: this.requiredErrorMessage,
        futureDate: 'Birth date must be in the past.',
        tooYoung: 'Birth date is too recent.',
        invalidFormat: 'Date format must be MM/DD/YYYY.'
      },
      firstName: {
        required: this.requiredErrorMessage,
        maxlength: `Entry must not exceed ${this.NAME_MAX_LENGTH} characters`,
        minlength: `Entry must be at least ${this.NAME_MIN_LENGTH} characters`
      },
      lastName: {
        required: this.requiredErrorMessage,
        maxlength: `Entry must not exceed ${this.NAME_MAX_LENGTH} characters`,
        minlength: `Entry must be at least ${this.NAME_MIN_LENGTH} characters`
      },
      email: {
        email: 'Please enter a valid email address',
        minlength: 'Email must be at least 6 characters',
        maxlength: 'Email must not exceed 255 characters'
      },
      gender: {
        required: this.requiredErrorMessage
      },
      address1: {
        required: this.requiredErrorMessage
      },
      city: {
        required: this.requiredErrorMessage
      },
      state: {
        required: this.requiredErrorMessage
      },
      zipCode: {
        required: this.requiredErrorMessage,
        pattern: 'ZIP code must be in the format 12345 or 12345-6789'
      }
    });

    // Assign individual error controls
    Object.assign(this.formErrors, personalFormErrors);

    const employmentErrors = createErrorControlMap(this.employmentForm, {
      event: {
        required: this.requiredErrorMessage
      },
      hireDate: {
        required: this.requiredErrorMessage,
        pattern: this.dateFormatMessage
      },
      billDivision: {
        required: this.requiredErrorMessage
      },
      annualSalary: {
        required: this.requiredErrorMessage,
        maxlength: 'Entry must not exceed 15 characters',
        pattern: 'Only numbers and currency symbols are allowed'
      },
      jobTitle: {
        maxlength: 'Entry must not exceed 100 characters'
      },
      employeeId: {
        maxlength: 'Entry must not exceed 50 characters',
        pattern: 'Only letters and numbers are allowed'
      }
    });
    this.formErrors.hireDate = employmentErrors.hireDate;
    this.formErrors.billDivision = employmentErrors.billDivision;
    this.formErrors.event = employmentErrors.event as unknown as ErrorControl;
    this.formErrors.annualSalary = employmentErrors.annualSalary as unknown as ErrorControl;
    this.formErrors.jobTitle = employmentErrors.jobTitle as unknown as ErrorControl;
    this.formErrors.employeeId = employmentErrors.employeeId as unknown as ErrorControl;
  }

  // :  Data members
  addEmployeeForm!: FormGroup;
  formErrors!: { [key: string]: ErrorControl };

  genderListOptions: DropdownOption<string>[] = [
    { key: 'male', displayName: 'Male' },
    { key: 'female', displayName: 'Female' },
    { key: 'non-binary', displayName: 'Non-Binary' },
    { key: 'unknown', displayName: 'Unknown' }
  ];

  smokerStatusOptions: DropdownOption<string>[] = [
    { key: 'yes', displayName: 'Yes' },
    { key: 'no', displayName: 'No' }
  ];

  // Radio options for smoker status - using RadioSelectData format
  smokerRadioOptions = [
    { id: 'smoker-yes', name: 'smokerStatus', value: 'smoker', label: 'Smoker' },
    { id: 'smoker-no', name: 'smokerStatus', value: 'non-smoker', label: 'Non-Smoker' }
  ];

  // State dropdown options (US states only, no 'All' option)
  stateOptions: DropdownOption<string>[] = StateUtilities.getStatesOnly().map(s => ({
    key: s.abbreviation,
    displayName: s.fullName
  }));

  billDivisionOptions: DropdownOption<string>[] = [];

  eventOptions: DropdownOption<string>[] = [
    { key: 'Select', displayName: 'Select' },
    { key: 'New hire', displayName: 'New hire' }
  ];

  // Character set for alphanumeric input (letters and numbers only)
  alphanumericCharSet = /[a-zA-Z0-9]/;
  // Character set for currency input (numbers and currency symbols only)
  currencyCharSet = /[0-9.$,]/;

  employeeCardTitle = 'Employee Information';
  hireDateError = 'Hire date must occur after birth date';
  dateFormatMessage = `Date format must be ${Defaults.dateFormatStringUI}`;
  NAME_MIN_LENGTH = 2;
  NAME_MAX_LENGTH = 50;
  SSN_MAX_LENGTH = 11;
  primaryButtonText = 'Back to Search Employees';
  secondaryButtonText = 'Save and Continue';
  requiredErrorMessage = `This field is required`;
  ssnPatternMessage = 'SSN must be in the format XXX-XX-XXXX';
  ssnPatternRegex = '^(?!666|000|9\\d{2})\\d{3}-(?!00)\\d{2}-(?!0{4})\\d{4}$';
  dateFormatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[01])\/\d{4}$/;

  employeeData = {
    firstName: {
      data: Constants.StringEmpty,
      label: 'First Name'
    },
    middleName: {
      data: Constants.StringEmpty,
      label: 'Middle'
    },
    lastName: {
      data: Constants.StringEmpty,
      label: 'Last Name'
    },
    ssn: {
      data: Constants.StringEmpty,
      label: 'SSN',
      placeholder: 'XXX-XX-XXXX'
    },
    birthDate: {
      data: Constants.StringEmpty,
      label: `Date of birth (${Defaults.dateFormatStringUI})`
    },
    gender: {
      data: ['Male', 'Female', 'Non-Binary', 'Other'],
      label: 'Gender'
    },
    hireDate: {
      data: Constants.StringEmpty,
      label: `Hire date  (${Defaults.dateFormatStringUI})`
    },
    smokerStatus: {
      data: ['Yes', 'No'],
      label: 'Smoker Status'
    },
    occupation: {
      data: Constants.StringEmpty,
      label: 'Occupation'
    },
    annualSalary: {
      data: Constants.StringEmpty,
      label: 'Annual Salary'
    },
    billDivision: {
      data: ['Division 1', 'Division 2', 'Division 3', 'Division 4', 'Division 5'],
      label: 'Bill Division'
    }
  };

  getErrorMap = (formGroup: FormGroup) =>
    createErrorControlMap(formGroup, {
      firstName: {
        required: this.requiredErrorMessage,
        maxlength: `Entry must not exceed ${this.NAME_MAX_LENGTH} characters`,
        minlength: `Entry must be at least ${this.NAME_MIN_LENGTH} characters`
      },
      lastName: {
        required: this.requiredErrorMessage,
        maxlength: `Entry must not exceed ${this.NAME_MAX_LENGTH} characters`,
        minlength: `Entry must be at least ${this.NAME_MIN_LENGTH} characters`
      },
      ssn: {
        required: this.requiredErrorMessage,
        pattern: this.ssnPatternMessage
      },
      birthDate: {
        required: this.requiredErrorMessage,
        pattern: this.dateFormatMessage
      },
      hireDate: {
        required: this.requiredErrorMessage,
        pattern: this.dateFormatMessage
      },
      gender: {
        required: this.requiredErrorMessage
      },
      annualSalary: {
        required: this.requiredErrorMessage
      },
      billDivision: {
        required: this.requiredErrorMessage
      }
    }) as object as {
      firstName: ErrorControl;
      lastName: ErrorControl;
      ssn: ErrorControl;
      birthDate: ErrorControl;
      hireDate: ErrorControl;
      gender: ErrorControl;
      annualSalary: ErrorControl;
      billDivision: ErrorControl;
    };

  formatSSN(value: string): string {
    const v = value.replace(/\D/g, Constants.StringEmpty).slice(0, 9);
    const arr = v.match(/(\d{1,3})(\d{1,2})?(\d{1,4})?/);
    if (arr) {
      return `${arr[1] || Constants.StringEmpty}${arr[2] ? '-' + arr[2] : Constants.StringEmpty}${arr[3] ? '-' + arr[3] : Constants.StringEmpty}`;
    }
    return Constants.StringEmpty;
  }

  onSSNInputChange(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const formattedSSN = this.formatSSN(inputValue);
    this.addEmployeeForm.controls['ssn'].setValue(formattedSSN, {
      emitEvent: false
    });
  }

  setupFormValidation(): void {
    // Update step validity when forms change
    this.personalInfoForm.statusChanges.subscribe((status) => {
      this.updateStep(0, status === 'VALID', this.personalInfoForm);
    });

    this.employmentForm.statusChanges.subscribe((status) => {
      this.updateStep(1, status === 'VALID', this.employmentForm);
    });

    this.contactForm.statusChanges.subscribe((status) => {
      this.updateStep(2, status === 'VALID', this.contactForm);
    });

    // Check initial validity
    this.updateStep(0, this.personalInfoForm.valid, this.personalInfoForm);
    this.updateStep(1, this.employmentForm.valid, this.employmentForm);
    this.updateStep(2, this.contactForm.valid, this.contactForm);
  }

  updateStep(stepIndex: number, isValid: boolean, form: FormGroup): void {
    const step = this.steps[stepIndex];
    step.valid = isValid;

    // Update unfilled count
    let unfilledCount = 0;
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (control && control.hasError('required') && !control.value) {
        unfilledCount++;
      }
    });
    step.unfilledCount = unfilledCount;

    // Mark step as visited if user has interacted with it
    if (this.currentStepIndex >= stepIndex) {
      step.visited = true;
    }
  }

  onStepChange(stepIndex: number): void {
    console.log('Step changed to:', stepIndex);
    this.currentStepIndex = stepIndex;

    // Mark current step as visited
    if (this.steps[stepIndex]) {
      this.steps[stepIndex].visited = true;
    }
  }

  onBeforeStepChange(event: { from: number; to: number; preventDefault: () => void }): void {
    // Mark forms as touched when leaving a step
    if (event.from === 0) {
      this.personalInfoForm.markAllAsTouched();
    } else if (event.from === 1) {
      this.employmentForm.markAllAsTouched();
    } else if (event.from === 2) {
      this.contactForm.markAllAsTouched();
    }
  }

  onContinueClick(): void {
    // Get the current form based on step index
    let currentForm: FormGroup | null = null;
    if (this.currentStepIndex === 0) {
      currentForm = this.personalInfoForm;
    } else if (this.currentStepIndex === 1) {
      currentForm = this.employmentForm;
    } else if (this.currentStepIndex === 2) {
      currentForm = this.contactForm;
    }

    // Mark all fields as touched to show validation errors
    if (currentForm) {
      currentForm.markAllAsTouched();
    }

    // Check if we're on the last step
    if (this.isLastStep()) {
      if (this.canSubmit()) {
        this.submitForm();
      }
      return;
    }

    // For non-last steps, always allow progression but update step state
    if (this.currentStepIndex < this.steps.length - 1) {
      // Mark current step as visited (this will show checkmark if valid)
      const currentStep = this.steps[this.currentStepIndex];
      currentStep.visited = true;

      // Move to next step
      this.currentStepIndex++;
      console.log('Continue clicked, moving to step:', this.currentStepIndex);
    }
  }

  onBackClick(): void {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      console.log('Back clicked, moving to step:', this.currentStepIndex);
    }
  }

  onCancelClick(): void {
    // Show the cancel confirmation modal
    this.showCancelModal = true;
  }

  onCancelConfirm(): void {
    // User confirmed cancellation
    this.showCancelModal = false;
    // Navigate back to the Add Employee search page
    const token = this.route.snapshot.queryParamMap.get('token') || '';
    this.router.navigate(['/benefits/employees/add'], { queryParams: { token } });
  }

  onCancelDismiss(): void {
    // User chose not to cancel
    this.showCancelModal = false;
  }

  getCurrentStep(): DxoFormStep | null {
    return this.steps[this.currentStepIndex] || null;
  }

  getTotalSteps(): number {
    return this.steps.length;
  }

  isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  canSubmit(): boolean {
    return this.personalInfoForm.valid && this.employmentForm.valid && this.contactForm.valid;
  }

  isCurrentStepValid(): boolean {
    if (this.currentStepIndex === 0) {
      return this.personalInfoForm.valid;
    } else if (this.currentStepIndex === 1) {
      return this.employmentForm.valid;
    } else if (this.currentStepIndex === 2) {
      return this.contactForm.valid;
    }
    return true;
  }

  canContinue(): boolean {
    // Step 1 (index 0) can always progress
    if (this.currentStepIndex === 0) {
      return true;
    }

    // Step 2 (index 1) requires Step 1 to be valid AND Step 2 to be valid
    if (this.currentStepIndex === 1) {
      const step1Valid = this.personalInfoForm.valid;
      const step2Valid = this.employmentForm.valid;
      return step1Valid && step2Valid;
    }

    // For other steps (3 and 4), check if all forms are valid
    if (this.isLastStep()) {
      return this.canSubmit();
    }

    // Default for any other steps
    return this.isCurrentStepValid();
  }

  getTotalUnfilledCount(): number {
    return this.steps.reduce((total, step) => {
      return total + (step.unfilledCount || 0);
    }, 0);
  }

  setupAnnualSalaryFormatter(): void {
    const ctrl = this.employmentForm.get('annualSalary');
    if (!ctrl) return;
    ctrl.valueChanges.subscribe((val) => {
      if (val === null || val === undefined) return;
      const input = String(val);
      const formatted = currencyStringCreate(input);
      if (input !== formatted) {
        ctrl.setValue(formatted, { emitEvent: false });
      }
    });
  }

  loadBillDivisions(): void {
    this.groupClient.billdivisions().subscribe((resp) => {
      const divisions = resp?.data ?? [];
      const options = divisions
        .filter((d) => !!d)
        .map((d) => ({ key: d.id ?? d.code ?? d.name ?? '', displayName: d.displayName ?? d.name ?? '' }));

      // Add "Select" as the first option
      this.billDivisionOptions = [{ key: 'Select', displayName: 'Select' }, ...options];

      // Set default to first actual bill division if available
      if (options.length > 0) {
        const firstDivision = options[0].key;
        this.employmentForm.get('billDivision')?.setValue(firstDivision);
        this.contactForm.get('billDivision')?.setValue(firstDivision);
      }
    });
  }

  getTimeOfService(): string {
    const date = this.employmentForm?.get('hireDate')?.value;
    if (!date) {
      return '--';
    }
    const hireDate = new Date(date);
    const today = new Date();
    if (isNaN(hireDate.getTime()) || hireDate >= today) {
      return 'N/A';
    }

    let years = today.getFullYear() - hireDate.getFullYear();
    let months = today.getMonth() - hireDate.getMonth();
    let days = today.getDate() - hireDate.getDate();

    if (days < 0) {
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
      months -= 1;
    }
    if (months < 0) {
      months += 12;
      years -= 1;
    }

    const parts: string[] = [];
    if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
    if (years === 0 && months === 0 && days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);

    return parts.length > 0 ? parts.join(', ') : 'N/A';
  }

  onAnnualSalaryBlur(): void {
    const ctrl = this.employmentForm.get('annualSalary');
    const raw = (ctrl?.value as string) ?? '';
    const formatted = currencyStringCreate(raw);
    ctrl?.setValue(formatted, { emitEvent: false });
  }

  submitForm(): void {
    if (this.canSubmit()) {
      console.log('Form VALID');
      console.log('Personal Info:', this.personalInfoForm.value);
      console.log('Employment Info:', this.employmentForm.value);
      console.log('Contact Info:', this.contactForm.value);
      // TODO: Submit to API
    } else {
      console.log('Form INVALID');
      // Mark all forms as touched to show validation errors
      this.personalInfoForm.markAllAsTouched();
      this.employmentForm.markAllAsTouched();
      this.contactForm.markAllAsTouched();
    }
  }

  getBillDivisionDisplayName(): string {
    const value = this.contactForm.get('billDivision')?.value;
    if (!value) {
      return 'Not provided';
    }
    const option = this.billDivisionOptions.find((opt) => opt.key === value);
    return option?.displayName || value;
  }

  getGenderDisplayName(): string {
    const value = this.personalInfoForm.get('gender')?.value;
    if (!value) {
      return 'Not provided';
    }
    const option = this.genderListOptions.find((opt) => opt.key === value);
    return option?.displayName || value;
  }

  getStateDisplayName(): string {
    const value = this.personalInfoForm.get('state')?.value;
    if (!value) {
      return 'Not provided';
    }
    const option = this.stateOptions.find((opt) => opt.key === value);
    return option?.displayName || value;
  }

  getSmokerStatusDisplayName(): string {
    const value = this.personalInfoForm.get('smokerStatus')?.value;
    if (!value) {
      return 'Not provided';
    }
    const option = this.smokerRadioOptions.find((opt) => opt.value === value);
    return option?.label || value;
  }

  getFormattedSSN(): string {
    return this.ssnValue || 'Not provided';
  }

  calculateAge(): string {
    const birthDateControl = this.personalInfoForm?.get('birthDate');
    if (!birthDateControl || !birthDateControl.value || birthDateControl.invalid) {
      return '';
    }

    const birthDate = new Date(birthDateControl.value);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return `${age} years old`;
  }
}
